import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  LegalSection,
  LegalListItem,
  legalH2,
  legalH3,
  legalP,
} from '@/components/legal/LegalLayout';

export function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - CriticalBridge.AI</title>
        <meta
          name="description"
          content="CriticalBridge.AI Terms of Service - Read our terms and conditions for using our workflow automation platform."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/terms" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Terms of Service
          </div>
          <h1 className="mt-4 text-4xl font-extrabold text-secondary-foreground sm:text-5xl">
            Terms of Service
          </h1>
          <div className="mt-3 font-mono text-xs text-secondary-foreground/60">
            Last updated: October 5, 2026
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto max-w-[74ch] px-4">
          <LegalSection>
            <h2 className={legalH2}>1. Acceptance of Terms</h2>
            <p className={legalP}>
              By accessing or using CriticalBridge.AI's website, desktop application, or workflow
              automation services (collectively, the "Services"), you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our
              Services.
            </p>
            <p className={legalP}>
              These Terms constitute a legally binding agreement between you and CriticalBridge.AI
              ("we," "us," or "our"). We may update these Terms from time to time, and your
              continued use of the Services constitutes acceptance of any changes.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>2. Service Description</h2>
            <p className={legalP}>
              CriticalBridge.AI provides a workflow automation platform that enables users to:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Create and manage automated workflows</LegalListItem>
              <LegalListItem>
                Connect and integrate with third-party applications and services
              </LegalListItem>
              <LegalListItem>Deploy AI agents to automate business processes</LegalListItem>
              <LegalListItem>Monitor and analyze workflow performance</LegalListItem>
              <LegalListItem>Collaborate with team members on automation projects</LegalListItem>
            </ul>
            <p className={legalP}>
              We reserve the right to modify, suspend, or discontinue any aspect of the Services at
              any time with reasonable notice.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>3. User Accounts</h2>
            <h3 className={legalH3}>Registration</h3>
            <p className={legalP}>
              To access certain features of our Services, you must create an account. When
              registering, you agree to:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Provide accurate, current, and complete information</LegalListItem>
              <LegalListItem>Maintain and promptly update your account information</LegalListItem>
              <LegalListItem>
                Be at least 18 years of age or the age of majority in your jurisdiction
              </LegalListItem>
              <LegalListItem>Use only one account per individual or organization</LegalListItem>
            </ul>

            <h3 className={legalH3}>Account Security</h3>
            <p className={legalP}>
              You are responsible for maintaining the confidentiality of your account credentials
              and for all activities that occur under your account. You agree to notify us
              immediately of any unauthorized use of your account.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>4. Acceptable Use</h2>
            <h3 className={legalH3}>Permitted Uses</h3>
            <p className={legalP}>You may use our Services for:</p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Business automation and workflow management</LegalListItem>
              <LegalListItem>Integration with authorized third-party services</LegalListItem>
              <LegalListItem>Data processing within applicable rate limits</LegalListItem>
              <LegalListItem>Collaboration with authorized team members</LegalListItem>
            </ul>

            <h3 className={legalH3}>Prohibited Uses</h3>
            <p className={legalP}>You may not use our Services to:</p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Violate any applicable laws or regulations</LegalListItem>
              <LegalListItem>Infringe on intellectual property rights of others</LegalListItem>
              <LegalListItem>Transmit malware, viruses, or harmful code</LegalListItem>
              <LegalListItem>Send spam, unsolicited messages, or phishing attempts</LegalListItem>
              <LegalListItem>Attempt to gain unauthorized access to our systems</LegalListItem>
              <LegalListItem>Interfere with or disrupt the Services or servers</LegalListItem>
              <LegalListItem>
                Reverse engineer, decompile, or disassemble our software
              </LegalListItem>
              <LegalListItem>
                Resell, sublicense, or redistribute the Services without authorization
              </LegalListItem>
              <LegalListItem>Use the Services in a manner that could harm minors</LegalListItem>
              <LegalListItem>Engage in any activity that is fraudulent or deceptive</LegalListItem>
            </ul>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>5. Subscription and Billing</h2>
            <h3 className={legalH3}>Paid Subscriptions</h3>
            <p className={legalP}>
              Paid subscriptions are billed in advance on a monthly or annual basis. By subscribing,
              you authorize us to charge your designated payment method for all fees incurred.
            </p>

            <h3 className={legalH3}>Automatic Renewal</h3>
            <p className={legalP}>
              Subscriptions automatically renew at the end of each billing period unless you cancel
              before the renewal date. You can cancel your subscription at any time through your
              account settings.
            </p>

            <h3 className={legalH3}>Refunds</h3>
            <p className={legalP}>
              We offer refunds within 30 days of your initial purchase for annual subscriptions.
              Monthly subscriptions are non-refundable. To request a refund, please contact
              billing@criticalbridge.ai.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>6. Intellectual Property</h2>
            <h3 className={legalH3}>Our Intellectual Property</h3>
            <p className={legalP}>
              The Services, including all software, content, designs, trademarks, and other
              materials, are owned by CriticalBridge.AI and are protected by intellectual property
              laws. We grant you a limited, non-exclusive, non-transferable license to use the
              Services in accordance with these Terms.
            </p>

            <h3 className={legalH3}>Your Content</h3>
            <p className={legalP}>
              You retain ownership of all data, workflows, and content you create using our Services
              ("Your Content"). By using our Services, you grant us a limited license to process
              Your Content solely for the purpose of providing the Services to you.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>7. Privacy</h2>
            <p className={legalP}>
              Your privacy is important to us. Our collection and use of your personal information
              is governed by our{' '}
              <Link to="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>8. Third-Party Services</h2>
            <p className={legalP}>
              Our Services may integrate with or contain links to third-party services. We are not
              responsible for the content, policies, or practices of any third-party services. Your
              use of third-party services is at your own risk and subject to their respective terms
              and conditions.
            </p>
            <p className={legalP}>
              When you connect third-party services to CriticalBridge.AI, you authorize us to access
              and process data from those services as necessary to provide our Services.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>9. Disclaimers</h2>
            <p className={legalP}>
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className={legalP}>
              We do not guarantee that the Services will be uninterrupted, error-free, or secure. We
              are not responsible for any damages resulting from your use of the Services or any
              third-party services integrated with our platform.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>10. Limitation of Liability</h2>
            <p className={legalP}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CRITICALBRIDGE.AI SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF
              PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICES.
            </p>
            <p className={legalP}>
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS
              PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>11. Indemnification</h2>
            <p className={legalP}>
              You agree to indemnify, defend, and hold harmless CriticalBridge.AI and its officers,
              directors, employees, and agents from any claims, damages, losses, or expenses
              (including reasonable attorneys' fees) arising from your use of the Services, your
              violation of these Terms, or your infringement of any third-party rights.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>12. Termination</h2>
            <h3 className={legalH3}>Termination by You</h3>
            <p className={legalP}>
              You may cancel your account at any time through your account settings or by contacting
              us. Upon cancellation, your right to access the Services will end.
            </p>

            <h3 className={legalH3}>Termination by Us</h3>
            <p className={legalP}>
              We may suspend or terminate your account if you violate these Terms or engage in
              conduct that we determine is harmful to other users, our business, or third parties.
            </p>

            <h3 className={legalH3}>Effect of Termination</h3>
            <p className={legalP}>
              Upon termination, your access to the Services will cease. You may request an export of
              your data within 30 days of termination. After 30 days, we may delete your data in
              accordance with our data retention policies.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>13. Governing Law and Disputes</h2>
            <p className={legalP}>
              These Terms are governed by the laws of the State of California, without regard to
              conflict of law principles. Any disputes arising from these Terms or your use of the
              Services shall be resolved in the state or federal courts located in San Francisco
              County, California.
            </p>
            <p className={legalP}>
              Before filing any claim, you agree to attempt to resolve the dispute informally by
              contacting us at legal@criticalbridge.ai. We will try to resolve the dispute within 30
              days.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>14. General Provisions</h2>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>
                <strong className="text-foreground">Entire Agreement:</strong> These Terms
                constitute the entire agreement between you and CriticalBridge.AI regarding the
                Services.
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Severability:</strong> If any provision of these
                Terms is found invalid, the remaining provisions will continue in effect.
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Waiver:</strong> Our failure to enforce any
                provision does not constitute a waiver of that provision.
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Assignment:</strong> You may not assign these
                Terms without our consent. We may assign these Terms without restriction.
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Force Majeure:</strong> We are not liable for
                delays or failures due to circumstances beyond our reasonable control.
              </LegalListItem>
            </ul>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>15. Contact Us</h2>
            <p className={legalP}>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="mt-4 rounded-lg border bg-card p-5">
              <p className="font-bold">CriticalBridge.AI</p>
              <p className="mt-1 text-sm text-muted-foreground">Email: legal@criticalbridge.ai</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Address: 123 Innovation Drive, San Francisco, CA 94105
              </p>
            </div>
          </LegalSection>
        </div>
      </section>
    </>
  );
}
